import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Maximize, Minimize, Monitor } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as AttemptApi from 'modules/attempt/api';
import { useMonitoringSocket, type TickerEvent } from 'modules/monitoring/useMonitoringSocket';
import { Avatar } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import EmptyState from 'components/ui/EmptyState';
import { PageHeader } from 'components/ui/page-header';
import { Panel } from 'components/ui/panel';
import { Progress } from 'components/ui/progress';
import { Skeleton } from 'components/ui/skeleton';
import { StatTile } from 'components/ui/stat-tile';
import { StatusBadge } from 'components/ui/status-badge';
import { useWorkspaceLive } from 'components/workspace/WorkspaceLive';
import { useIsMobile } from 'hooks/use-media-query';
import { formatClockDuration } from 'lib/timeFormat';
import { cn } from 'lib/utils';

/** How a candidate or an event reads: fine, needs a look (window switches), or a rule broken (copying, screenshots, leaving full screen). */
type Tone = 'ok' | 'warning' | 'error';

interface Candidate {
  id: string;
  name: string;
  answered: number;
  total: number;
  progress: number;
  /** TAB_SWITCH + FOCUS_LOST events (the API's `violations`). */
  windowSwitches: number;
  /** COPY_PASTE + SCREENSHOT + FULLSCREEN_EXIT events (the API's `warnings`). */
  ruleBreaks: number;
  tone: Tone;
}

const EMPTY_SUMMARY: AttemptApi.MonitoringSummary = { inProgress: 0, submitted: 0, flagged: 0, timeLeftSec: null };
const EMPTY_ITEMS: AttemptApi.ActiveAttemptItem[] = [];
const EMPTY_EVENTS: TickerEvent[] = [];
const TONE_ORDER: Record<Tone, number> = { error: 0, warning: 1, ok: 2 };
const SKELETON_CARDS = 6;

const DOT: Record<Tone, string> = {
  ok: 'bg-success-600',
  warning: 'bg-warning-500',
  error: 'bg-error-600',
};
const CARD_BORDER: Record<Tone, string> = {
  ok: 'border-border',
  warning: 'border-warning-200 dark:border-warning-900',
  error: 'border-error-200 dark:border-error-900',
};
const REASON_TEXT: Record<Exclude<Tone, 'ok'>, string> = {
  warning: 'text-warning-700 dark:text-warning-400',
  error: 'text-error-700 dark:text-error-400',
};

const EVENT_LABEL: Record<string, string> = {
  TAB_SWITCH: 'event_tab_switch',
  FOCUS_LOST: 'event_focus_lost',
  COPY_PASTE: 'event_copy_paste',
  SCREENSHOT: 'event_screenshot',
  FULLSCREEN_EXIT: 'event_fullscreen_exit',
};
// Same split as the candidate's reason: leaving the window needs a look,
// the rest breaks a rule.
const EVENT_TONE: Record<string, Tone> = {
  TAB_SWITCH: 'warning',
  FOCUS_LOST: 'warning',
  COPY_PASTE: 'error',
  SCREENSHOT: 'error',
  FULLSCREEN_EXIT: 'error',
};

type Translate = (key: string, opts?: Record<string, unknown>) => string;

function toCandidate(a: AttemptApi.ActiveAttemptItem, guest: string): Candidate {
  const windowSwitches = a.violations || 0;
  const ruleBreaks = a.warnings || 0;
  return {
    id: a.id,
    name: a.candidate?.name || guest,
    answered: a.answerCount || 0,
    total: a.totalQuestions || 0,
    progress: a.progress || 0,
    windowSwitches,
    ruleBreaks,
    tone: ruleBreaks > 0 ? 'error' : windowSwitches > 0 ? 'warning' : 'ok',
  };
}

/** "Oyna almashtirildi ×2": the most serious thing that happened, with how often. */
function reasonText(c: Candidate, t: Translate): string | null {
  const times = (n: number) => (n > 1 ? ` ×${n}` : '');
  if (c.ruleBreaks > 0) return `${t('monitoring_reason_violation')}${times(c.ruleBreaks)}`;
  if (c.windowSwitches > 0) return `${t('monitoring_reason_window')}${times(c.windowSwitches)}`;
  return null;
}

const hhmm = (at: number): string => {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function eventView(e: TickerEvent, t: Translate): { label: string; dot: string } {
  if (e.kind === 'started') return { label: t('monitoring_event_started'), dot: DOT.ok };
  if (e.kind === 'submitted') return { label: t('monitoring_event_submitted'), dot: 'bg-foreground' };
  const key = e.type ? EVENT_LABEL[e.type] : undefined;
  return {
    label: key ? t(key) : t('monitoring_event_violation'),
    dot: DOT[(e.type && EVENT_TONE[e.type]) || 'warning'],
  };
}

function CandidateCard({ c }: { c: Candidate }) {
  const { t } = useTranslation();
  const reason = reasonText(c, t);
  return (
    <article className={cn('flex min-w-0 flex-col gap-2.5 rounded-lg border bg-card p-3.5', CARD_BORDER[c.tone])}>
      <div className="flex items-center gap-2.5">
        <Avatar name={c.name} size="sm" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{c.name}</span>
        <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', DOT[c.tone])} />
      </div>
      {reason && c.tone !== 'ok' ? (
        // Stretched across the card, as drawn.
        <StatusBadge tone={c.tone} dot={false}>
          {reason}
        </StatusBadge>
      ) : (
        <span className="text-[12.5px] leading-5 text-muted-foreground">{t('monitoring_no_issues')}</span>
      )}
      <div className="flex items-center gap-2.5">
        <Progress value={c.total > 0 ? c.progress : 0} size="sm" className="min-w-0 flex-1" />
        <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
          {c.total > 0 ? `${c.answered} / ${c.total}` : c.answered}
        </span>
      </div>
    </article>
  );
}

function CandidateRow({ c }: { c: Candidate }) {
  const { t } = useTranslation();
  const reason = reasonText(c, t);
  const progress =
    c.total > 0
      ? t('monitoring_answered_of', { answered: c.answered, total: c.total })
      : t('monitoring_answered', { count: c.answered });
  return (
    <li className="flex items-center gap-3 px-3.5 py-3">
      <Avatar name={c.name} size="sm" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-medium leading-5 text-foreground">{c.name}</span>
        <span
          className={cn(
            'truncate text-[12.5px] leading-4',
            reason && c.tone !== 'ok' ? REASON_TEXT[c.tone] : 'text-muted-foreground',
          )}
        >
          {reason ?? progress}
        </span>
      </span>
      <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', DOT[c.tone])} />
    </li>
  );
}

function CardSkeleton() {
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-card p-3.5">
      <div className="flex items-center gap-2.5">
        <Skeleton variant="circular" className="h-8 w-8" />
        <Skeleton className="h-4 w-32" />
      </div>
      <Skeleton className="h-3.5 w-24" />
      <Skeleton className="h-1 w-full" />
    </div>
  );
}

interface EventLogProps {
  ticker: TickerEvent[];
  names: Map<string, string>;
  /** Desktop only: the proctor actions that still wait for a backend. */
  withActions?: boolean;
}

function EventLog({ ticker, names, withActions }: EventLogProps) {
  const { t } = useTranslation();
  const gapId = useId();
  const flags = ticker.filter((e) => e.kind === 'violation').length;
  return (
    <Panel
      title={t('monitor_event_log')}
      aside={
        flags > 0 ? (
          <StatusBadge tone="warning" dot={false}>
            {t('monitor_flags_count', { count: flags })}
          </StatusBadge>
        ) : undefined
      }
      flush
    >
      {ticker.length === 0 ? (
        <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">{t('monitor_no_events')}</p>
      ) : (
        <ol className={cn('divide-y divide-border', withActions && 'max-h-[520px] overflow-y-auto scrollbar-thin')}>
          {ticker.map((e) => {
            const view = eventView(e, t);
            return (
              <li
                key={e.id}
                className="grid grid-cols-[44px_8px_minmax(0,1fr)] items-start gap-x-2.5 px-4 py-2.5 text-[13px]"
              >
                <span className="font-mono text-xs leading-4 tabular-nums text-muted-foreground">{hhmm(e.at)}</span>
                <span aria-hidden className={cn('mt-1 h-2 w-2 rounded-full', view.dot)} />
                <span className="flex min-w-0 flex-col gap-px leading-4">
                  <span className="truncate font-medium text-foreground">
                    {names.get(e.attemptId || '') || e.candidateName || t('guest')}
                  </span>
                  <span className="text-muted-foreground">{view.label}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {withActions && (
        // Neither action has a backend endpoint yet (monitoring.gateway.ts only
        // supports join/leave and one-way attempt:* broadcasts), so both stay
        // disabled and say why instead of faking a success.
        <div className="flex gap-2 border-t border-border px-4 py-3" title={t('monitor_action_gap')}>
          <span id={gapId} className="sr-only">
            {t('monitor_action_gap')}
          </span>
          <Button variant="outline" size="xs" disabled aria-describedby={gapId}>
            {t('monitor_message_all')}
          </Button>
          <Button variant="outline" size="xs" disabled aria-describedby={gapId}>
            {t('monitor_pause')}
          </Button>
        </div>
      )}
    </Panel>
  );
}

interface LiveMonitoringProps {
  defaultTestId?: string;
}

/**
 * Live proctoring for one test. Counters come from the server-computed
 * summary (which spans every attempt status); the socket feed powers the
 * event log and triggers refetches. The workspace header shows "Jonli".
 */
const LiveMonitoring: React.FC<LiveMonitoringProps> = ({ defaultTestId }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const testId = defaultTestId;
  const rootRef = useRef<HTMLDivElement>(null);
  const [bigScreen, setBigScreen] = useState(false);

  // When the summary was fetched — the countdown ticks down locally from the
  // server-computed timeLeftSec until the next poll/socket refetch.
  const fetchedAtRef = useRef(Date.now());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['monitoring', 'active', testId],
    queryFn: async () => {
      const { data: res } = await AttemptApi.Active({ testId });
      fetchedAtRef.current = Date.now();
      return res || { items: [], events: [], summary: EMPTY_SUMMARY };
    },
    refetchInterval: 15_000,
  });
  const activeAttempts = data?.items ?? EMPTY_ITEMS;
  const initialEvents = data?.events ?? EMPTY_EVENTS;
  const summary = data?.summary ?? EMPTY_SUMMARY;

  // One-second heartbeat so the time-left counter ticks even between polls.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { connected, ticker } = useMonitoringSocket(testId, () => refetch(), initialEvents);
  useWorkspaceLive(connected);

  const guest = t('guest');
  const candidates = useMemo(
    () =>
      activeAttempts
        .map((a) => toCandidate(a, guest))
        .sort((x, y) => TONE_ORDER[x.tone] - TONE_ORDER[y.tone] || y.progress - x.progress),
    [activeAttempts, guest],
  );

  // Names outlive the attempt's time in the list, so a candidate who just
  // submitted keeps their name in the log instead of turning into "Guest".
  const namesRef = useRef(new Map<string, string>());
  const names = useMemo(() => {
    candidates.forEach((c) => namesRef.current.set(c.id, c.name));
    return namesRef.current;
  }, [candidates]);

  // Tick the server-computed deadline down locally between polls; never let a
  // stale poll re-inflate it, and hold at 00:00 once expired.
  const timeLeft =
    summary.timeLeftSec === null
      ? null
      : Math.max(0, summary.timeLeftSec - Math.floor((Date.now() - fetchedAtRef.current) / 1000));

  // The browser can leave fullscreen behind our back (Escape, browser chrome),
  // so big-screen state follows `document.fullscreenElement`.
  useEffect(() => {
    const handleFullscreenChange = () => setBigScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleBigScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await rootRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      // Fullscreen rejected (denied, sandboxed iframe): no fullscreenchange
      // will fire, so toggle the big-screen layout by hand — it's useful
      // without real OS fullscreen.
      setBigScreen((b) => !b);
    }
  };

  const loading = isLoading && !data;
  const figure = (value: React.ReactNode) => (loading ? '—' : value);
  const tiles = (
    <>
      <StatTile label={t('monitor_counter_inprogress')} value={figure(summary.inProgress)} />
      <StatTile label={t('monitor_counter_submitted')} value={figure(summary.submitted)} />
      <StatTile
        label={t('monitor_counter_flagged')}
        value={figure(summary.flagged)}
        tone={summary.flagged > 0 ? 'warning' : undefined}
      />
      <StatTile
        label={t('monitor_counter_timeleft')}
        value={figure(timeLeft === null ? '—' : timeLeft > 0 ? formatClockDuration(timeLeft) : '00:00')}
      />
    </>
  );

  const failed = isError && !data;
  const failedState = (
    <EmptyState
      icon={<AlertTriangle />}
      title={t('failed_to_load')}
      secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
    />
  );
  const emptyState = (
    <EmptyState
      icon={<Monitor />}
      title={t('no_active_sessions_at_the_moment')}
      description={t('monitoring_empty_hint')}
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2.5">{tiles}</div>
        <Panel
          title={t('respondents')}
          aside={loading || failed ? undefined : t('monitoring_candidates_count', { count: candidates.length })}
          flush
        >
          {loading ? (
            <ul className="divide-y divide-border">
              {Array.from({ length: 3 }, (_, i) => (
                <li key={i} className="flex items-center gap-3 px-3.5 py-3">
                  <Skeleton variant="circular" className="h-8 w-8" />
                  <span className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-20" />
                  </span>
                </li>
              ))}
            </ul>
          ) : failed ? (
            failedState
          ) : candidates.length === 0 ? (
            emptyState
          ) : (
            <ul className="divide-y divide-border">
              {candidates.map((c) => (
                <CandidateRow key={c.id} c={c} />
              ))}
            </ul>
          )}
        </Panel>
        <EventLog ticker={ticker} names={names} />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn('flex flex-col gap-4 bg-card', bigScreen && 'h-full overflow-y-auto p-6 scrollbar-thin')}
    >
      <PageHeader
        title={t('monitor_title')}
        actions={
          <Button variant="outline" className="gap-1.5" aria-pressed={bigScreen} onClick={toggleBigScreen}>
            {bigScreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            {t('monitor_bigscreen')}
          </Button>
        }
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{tiles}</div>
      <div className={cn('grid items-start gap-3.5', !bigScreen && 'lg:grid-cols-[minmax(0,1fr)_300px]')}>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: SKELETON_CARDS }, (_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : failed || candidates.length === 0 ? (
          <div className="rounded-lg border border-border bg-card">{failed ? failedState : emptyState}</div>
        ) : (
          <div
            className={cn(
              'grid gap-3',
              bigScreen ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4' : 'sm:grid-cols-2 xl:grid-cols-3',
            )}
          >
            {candidates.map((c) => (
              <CandidateCard key={c.id} c={c} />
            ))}
          </div>
        )}
        {!bigScreen && <EventLog ticker={ticker} names={names} withActions />}
      </div>
    </div>
  );
};

export default LiveMonitoring;
